import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Clock, Check, X as XIcon, MessageCircle, Phone, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { PackageCard } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { formatINR, waLink, SITE } from "@/lib/site";

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [enquireOpen, setEnquireOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setPkg(null); setNotFound(false); setActiveImg(0);
    api.get(`/packages/${slug}`)
      .then((r) => {
        setPkg(r.data);
        document.title = `${r.data.title} — click2trip`;
        api.get("/packages", { params: { category: r.data.category } })
          .then((res) => setRelated(res.data.filter((p) => p.slug !== slug).slice(0, 3)));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="package-not-found">
        <h1 className="font-display text-4xl font-black mb-4">Package not found</h1>
        <Link to="/packages" className="text-sun font-semibold hover:underline" data-testid="back-to-packages-link">Browse all packages</Link>
      </div>
    );
  }
  if (!pkg) {
    return <div className="pt-40 pb-32 max-w-7xl mx-auto px-6 animate-pulse"><div className="h-[50vh] bg-sand rounded-3xl" /></div>;
  }

  const images = pkg.gallery?.length ? pkg.gallery : [pkg.image];
  const save = pkg.original_price && pkg.original_price > pkg.price ? pkg.original_price - pkg.price : 0;
  const waMsg = `Hi click2trip! I'd like to book "${pkg.title}" (${pkg.duration_days}D/${pkg.nights}N). Please share availability and next steps.`;

  return (
    <div className="pt-28 pb-24" data-testid="package-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <div className="flex flex-wrap items-center gap-3 text-sm text-ink/60 mb-4">
                <span className="capitalize bg-sand/70 px-3 py-1 rounded-full">{pkg.category}</span>
                {pkg.type?.map((t) => <span key={t} className="capitalize border border-border px-3 py-1 rounded-full">{t}</span>)}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight mb-3" data-testid="package-title">{pkg.title}</h1>
              <div className="flex items-center gap-4 text-sm text-ink/60 mb-8">
                <span className="flex items-center gap-1.5"><MapPin size={14} className="text-sun" />{pkg.destination}</span>
                <span className="flex items-center gap-1.5"><Clock size={14} className="text-sun" />{pkg.duration_days} days / {pkg.nights} nights</span>
                <span className="flex items-center gap-1.5" data-testid="package-rating"><Star size={14} className="fill-sun text-sun" />{pkg.rating} ({pkg.reviews} reviews)</span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="relative overflow-hidden rounded-3xl aspect-[16/9]" data-testid="package-gallery">
                <motion.img key={activeImg} src={images[activeImg]} alt={`${pkg.title} gallery image ${activeImg + 1}`}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                  className="absolute inset-0 w-full h-full object-cover" />
              </div>
              {images.length > 1 && (
                <div className="flex gap-3 mt-3">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                      className={`relative w-24 aspect-video rounded-xl overflow-hidden transition-all ${i === activeImg ? "ring-2 ring-sun" : "opacity-60 hover:opacity-100"}`}>
                      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Reveal>

            <Reveal delay={0.15}>
              <h2 className="font-display text-2xl font-black mt-12 mb-5">Trip highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="package-highlights">
                {pkg.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-2.5 bg-sand/50 rounded-xl px-4 py-3 text-sm">
                    <Check size={15} className="text-sun shrink-0 mt-0.5" /> {h}
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <h2 className="font-display text-2xl font-black mt-12 mb-5">Day-by-day itinerary</h2>
              <Accordion type="single" collapsible defaultValue="day-1" className="space-y-3" data-testid="itinerary-accordion">
                {pkg.itinerary.map((day) => (
                  <AccordionItem key={day.day} value={`day-${day.day}`} className="bg-white border border-border rounded-2xl px-5 overflow-hidden">
                    <AccordionTrigger data-testid={`itinerary-day-${day.day}`} className="hover:no-underline py-4">
                      <span className="flex items-center gap-4 text-left">
                        <span className="font-display text-sun font-black text-lg shrink-0 w-14">Day {day.day}</span>
                        <span className="font-semibold">{day.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-ink/60 text-sm leading-relaxed pb-5 pl-[4.5rem]">{day.desc}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="grid sm:grid-cols-2 gap-6 mt-12">
                <div className="bg-white border border-border rounded-2xl p-6" data-testid="inclusions-list">
                  <h3 className="font-display text-lg font-bold mb-4 text-ocean">What's included</h3>
                  <ul className="space-y-2.5 text-sm text-ink/70">
                    {pkg.inclusions.map((i) => <li key={i} className="flex gap-2.5"><Check size={15} className="text-green-600 shrink-0 mt-0.5" />{i}</li>)}
                  </ul>
                </div>
                <div className="bg-white border border-border rounded-2xl p-6" data-testid="exclusions-list">
                  <h3 className="font-display text-lg font-bold mb-4 text-ocean">Not included</h3>
                  <ul className="space-y-2.5 text-sm text-ink/70">
                    {pkg.exclusions.map((i) => <li key={i} className="flex gap-2.5"><XIcon size={15} className="text-red-500 shrink-0 mt-0.5" />{i}</li>)}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-32">
              <Reveal delay={0.2}>
                <div className="bg-ink text-bone rounded-3xl p-7 grain relative overflow-hidden" data-testid="booking-card">
                  <p className="text-bone/50 text-xs uppercase tracking-[0.2em] mb-2">Starting from</p>
                  <div className="flex items-baseline gap-3 mb-1">
                    <p className="font-display text-4xl font-black" data-testid="package-price">{formatINR(pkg.price)}</p>
                    {save > 0 && <p className="text-bone/40 line-through">{formatINR(pkg.original_price)}</p>}
                  </div>
                  <p className="text-bone/60 text-xs mb-1">per person, twin sharing</p>
                  {save > 0 && <p className="text-sun text-sm font-semibold mb-5" data-testid="package-savings">You save {formatINR(save)}</p>}
                  <div className="space-y-3 mt-6">
                    <button onClick={() => setEnquireOpen(true)} data-testid="enquire-now-btn"
                      className="w-full bg-sun text-bone font-semibold py-3.5 rounded-full hover:bg-[#e85a28] hover:scale-[1.02] active:scale-[0.98] transition-all">
                      Enquire Now
                    </button>
                    <a href={waLink(waMsg)} target="_blank" rel="noreferrer" data-testid="book-now-btn"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:bg-[#1fb857] hover:scale-[1.02] active:scale-[0.98] transition-all">
                      <MessageCircle size={16} /> Book on WhatsApp
                    </a>
                    <a href={`tel:${SITE.phoneRaw}`} data-testid="call-expert-btn"
                      className="w-full inline-flex items-center justify-center gap-2 border border-bone/20 text-bone/80 text-sm font-medium py-3 rounded-full hover:border-sun hover:text-sun transition-colors">
                      <Phone size={14} /> Call {SITE.phoneDisplay}
                    </a>
                  </div>
                  <div className="mt-6 pt-5 border-t border-bone/10 text-xs text-bone/50 space-y-2">
                    <p>✓ Free date changes up to 15 days before</p>
                    <p>✓ Best price guarantee</p>
                    <p>✓ 24/7 trip manager on WhatsApp</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-24" data-testid="related-packages">
            <h2 className="font-display text-3xl font-black mb-8">More {pkg.category} trips</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p) => <PackageCard key={p.slug} pkg={p} />)}
            </div>
          </div>
        )}
      </div>
      <EnquiryDialog open={enquireOpen} onOpenChange={setEnquireOpen} pkg={pkg} />
    </div>
  );
}
